import { z } from 'zod';

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

export const billingOrgIdParam = z.object({
  organizationId: z.string().uuid(),
});

export const subscriptionItemParams = z.object({
  organizationId: z.string().uuid(),
  itemId: z.string(),
});

// ---------------------------------------------------------------------------
// Bodies
// ---------------------------------------------------------------------------

export const spendCapBody = z.object({
  enabled: z.boolean(),
});

export const growthPacksBody = z.object({
  quantity: z.number().int().min(1).max(100).default(1),
});

export const portalSessionBody = z.object({
  return_url: z.string().url().optional(),
});

export const checkoutSessionBody = z.object({
  tier: z.enum(['solo', 'team']),
});

export const updateBillingCurrencyBody = z.object({
  currency: z.enum(['USD', 'BRL']),
});

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const invoicesQuery = z.object({
  limit: z.string().regex(/^\d+$/).optional(),
});

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type BillingOrgIdParam = z.infer<typeof billingOrgIdParam>;
export type SubscriptionItemParams = z.infer<typeof subscriptionItemParams>;
export type SpendCapBody = z.infer<typeof spendCapBody>;
export type GrowthPacksBody = z.infer<typeof growthPacksBody>;
export type PortalSessionBody = z.infer<typeof portalSessionBody>;
export type CheckoutSessionBody = z.infer<typeof checkoutSessionBody>;
export type UpdateBillingCurrencyBody = z.infer<typeof updateBillingCurrencyBody>;
export type InvoicesQuery = z.infer<typeof invoicesQuery>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface BillingOrganizationItem {
  organization_id: string;
  organization_name: string;
  billing_currency: string;
  created_at: string;
  organization_admin_user: {
    is_owner: boolean;
    enabled: boolean;
  }[];
}

export interface BillingOrganizationsResponse {
  success: true;
  data: BillingOrganizationItem[];
}

/**
 * The two closed billing unions, declared once. The Rekor entitlement projection
 * (below) and `BillingRecord` both spell them, and a third hand-written copy is how
 * `cancelled`/`canceled` drift starts.
 */
export const planKeySchema = z.enum(['free', 'solo', 'team']);
export const billingStatusSchema = z.enum(['active', 'past_due', 'cancelled']);
export type PlanKey = z.infer<typeof planKeySchema>;
export type BillingStatusKey = z.infer<typeof billingStatusSchema>;

/**
 * How the org's entitlement is PAID FOR — deliberately not the same question as `plan_key`,
 * which says what it is entitled TO.
 *
 * `comped` exists because the two were conflated: a platform-console override wrote
 * `plan_type: 'paid'` on an org with no Stripe subscription, producing a row byte-identical
 * to a corrupted paid one — no `current_period_start/end`, so no entitlement window, so no
 * Rekor projection and a 503 on every Data/Bases provisioning operation (production, org
 * 0de5878a, 2026-08-18). A comped org is entitled exactly like a paid one; what it lacks is
 * an invoice, and that is what this value names.
 */
export const billingPlanTypeSchema = z.enum(['free', 'paid', 'comped']);
export type BillingPlanType = z.infer<typeof billingPlanTypeSchema>;

/**
 * Does this plan type carry the SELF-RENEWING rolling window (`free_plan_started_at` plus a
 * fixed period, advanced by OrgDO's lazy rollover) rather than a Stripe billing period?
 *
 * The one place that question is answered, because three call sites must agree or the org
 * breaks in a different way at each: the rollover (a window that never advances), the
 * operations-quota read (a `resets_at` of null), and the Rekor projection's window source
 * (an unbuildable projection). Comped rows have no Stripe period to read, so they take the
 * same rolling window the free tier already owns — no new machinery, and renewal arrives via
 * the same lazy rollover.
 */
export function isRollingWindowPlanType(planType: string | null | undefined): boolean {
  return planType === 'free' || planType === 'comped';
}

/**
 * Is this row actually being billed by Stripe RIGHT NOW — `plan_type: 'paid'` with a
 * billing period that has not already elapsed?
 *
 * `plan_type` alone cannot answer it, and that gap is what the comped incident was made
 * of. `cancelBilling` leaves `current_period_end` at the ended subscription's date (only
 * the plan and the status change), so a console override that wrote `paid` back over such
 * a row produces a period that is real but PAST. Its projection then carries a
 * `window_resets_at` already behind the clock — permanently stale, with Rekor's import
 * gate refusing indefinitely and neither the sweep (the rebuild is byte-identical, so the
 * idempotency skip drops it) nor the renewal webhook (there is no subscription to renew)
 * able to converge.
 *
 * So the question this answers is "is Stripe going to move this window?" — the test behind
 * the admin plan PATCH's refusal, where a human is present to read the message. Deciding
 * whether to make an UNATTENDED write against a row is a different, more conservative
 * question; `isStrandedPaidRow` below is that one.
 */
export function isStripeBilledPlan(
  planType: string | null | undefined,
  currentPeriodEnd: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (planType !== 'paid' || !currentPeriodEnd) return false;
  const endMs = Date.parse(currentPeriodEnd);
  return Number.isFinite(endMs) && endMs > nowMs;
}

/**
 * Does this row say `paid` while PROVABLY having no subscription behind it?
 *
 * The conservative twin of `isStripeBilledPlan`, and the two are deliberately not each
 * other's negation. That one asks "is Stripe going to move this window?" and is what a
 * human-facing refusal uses, because a human is present to read the message. This one asks
 * "can we be certain nobody is paying?" and is what unattended writes use — the fleet
 * repair, and the console's decision to OFFER a comp control at all.
 *
 * An elapsed period alone is NOT proof. Stripe dunning leaves a real subscriber sitting at
 * `plan_type: 'paid'` with `subscription_status: 'past_due'` and an elapsed period for days
 * or weeks (`handlePaymentFailed` writes only the status), and a renewal webhook can simply
 * be late. Comping such an org would erase the arrears marker, restamp its window — Rekor's
 * counter-reset trigger — and take a paying customer off the Stripe meter. Only two shapes
 * are proof: no period was ever written, or the subscription is cancelled.
 */
export function isStrandedPaidRow(
  planType: string | null | undefined,
  currentPeriodEnd: string | null | undefined,
  subscriptionStatus: string | null | undefined,
): boolean {
  if (planType !== 'paid') return false;
  if (!currentPeriodEnd) return true;
  const status = String(subscriptionStatus ?? 'active').toLowerCase();
  return status === 'cancelled' || status === 'canceled' || status === 'incomplete_expired';
}

export interface BillingRecord {
  organization_id: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  plan_type: BillingPlanType;
  plan_key: PlanKey;
  plan_kind: 'free' | 'base' | 'addon';
  billing_cycle: 'monthly' | 'annual';
  display_name?: string;
  period_start: string | null;
  period_end: string | null;
  spend_cap_enabled: boolean;
  billing_status: BillingStatusKey;
  created_at: string;
  updated_at: string;
}

export interface BillingMetric {
  billing_metric_id: string;
  organization_id: string;
  metric_type: 'messages' | 'users' | 'storage' | 'api_calls';
  monthly_included: number;
  consumed_subscription: number;
  alert_80_sent: boolean;
  alert_95_sent: boolean;
  alert_100_sent: boolean;
  last_consumed_at: string | null;
  last_reset_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BillingMetricsHistory {
  history_id: string;
  organization_id: string;
  period_start: string;
  period_end: string;
  metric_type: 'messages' | 'users' | 'storage' | 'api_calls';
  monthly_included: number;
  consumed_subscription: number;
  created_at: string;
}

export interface BillingData {
  billing: BillingRecord;
  metrics: BillingMetric[] | null;
  history: BillingMetricsHistory[] | null;
  growth_pack: {
    operations_per_unit: number;
  } | null;
  is_owner: boolean;
  billing_currency: string;
}

export interface GetBillingDataResponse {
  success: true;
  data: BillingData;
}

export interface UpdateSpendCapResponse {
  success: true;
  data: { spend_cap_enabled: boolean };
}

export interface AddGrowthPackResponse {
  success: true;
  data: {
    subscription_item_id: string;
    quantity: number;
    messages_added: number;
    seats_added: number;
  };
}

export interface UpdateBillingCurrencyResponse {
  success: true;
  data: { currency: 'USD' | 'BRL' };
}

export interface RemoveSubscriptionItemResponse {
  success: true;
  data: { removed: string };
}

export interface PortalSessionResponse {
  success: true;
  data: { url: string };
}

export interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void' | null;
  created: number;
  period_start: number;
  period_end: number;
  invoice_pdf: string | null | undefined;
  hosted_invoice_url: string | null | undefined;
}

export interface GetInvoicesResponse {
  success: true;
  data: Invoice[];
}

export interface CheckoutSessionResponse {
  success: true;
  data: { url: string | null; mode: 'portal' | 'checkout'; session_id?: string };
}

// ---------------------------------------------------------------------------
// Rekor entitlement projection (docs/reference/rekor-platform-contracts.md §2)
//
// WayAI is the sole writer of Rekor's `entitlement:{org_id}` KV key (§2.1); Rekor
// owns `usage:{org_id}` and never authors this shape. The schema lives here rather
// than beside the writer so `contract_version` refusal on the Rekor side is a schema
// parse instead of hand-rolled field checks in two repos (§2.2).
// ---------------------------------------------------------------------------

/** Shape version. Rekor refuses to interpret a higher one (§2.3). */
export const REKOR_ENTITLEMENT_CONTRACT_VERSION = 1;

/** KV key prefix, unchanged from Rekor's existing `entitlement:` literal (§2.1). */
export const REKOR_ENTITLEMENT_KEY_PREFIX = 'entitlement:';

export const entitlementProjectionSchema = z
  .object({
    contract_version: z.literal(REKOR_ENTITLEMENT_CONTRACT_VERSION),
    /**
     * Monotonic per org, WayAI-assigned. An ordering guard for concurrent WayAI
     * writers (a plan change racing a token mint) — NOT a cross-repo CAS. Gaps are
     * expected: a version is allocated per push attempt, and an idempotency skip
     * (§2.4) burns none while a failed KV put burns one.
     */
    projection_version: z.number().int().nonnegative(),
    /** Diagnostic only — never gates. Freshness is `window_resets_at`'s job (§2.2). */
    written_at: z.string().datetime(),

    /** The WayAI org UUID. The `account_id` spelling is Rekor's documented carve-out. */
    account_id: z.string().uuid(),
    /** The EFFECTIVE plan, not the subscribed one (§2.2.1). Never gates. */
    plan_key: planKeySchema,
    /** Informational only (§2.2.1). `cancelled` spelling matches `BillingRecord`. */
    billing_status: billingStatusSchema,

    /** Tenths, matching Rekor's meter granularity (write 10, point-read 1, list/SQL 10). */
    ops_tenths_included: z.number().int().nonnegative(),
    max_records: z.number().int().nonnegative(),
    /** Its own field, no longer derived from `max_records` (§2.2). */
    import_rows_included: z.number().int().nonnegative(),
    /** REQUIRED — absence is malformed, never coerced to `false` (§2.2.2). */
    production_bases: z.boolean(),

    /** Opaque to Rekor; compared for equality only, to reset `usage:` counters (§2.3). */
    window_key: z.string().min(1),
    /**
     * The window's TRUE lower bound, for the reconcile's `operations_log` sum (§2.2).
     *
     * OPTIONAL, and that is not a rollout convenience — it is what "never gates" means
     * on the writer's side. The tier sources can be absent (a paid row carrying
     * `current_period_end` with no `current_period_start`) and inverted pairs are a
     * §2.2 diagnostic, so requiring it here would let an inert field throw the strict
     * parse and leave the org with NO projection — permanently stale under §2.3, with
     * §2.5's fail-closed gates refusing base creation and 503-ing every import. The
     * writer emits it whenever it has a sound value and omits it otherwise; Rekor's
     * observed-flip fallback covers the gap, and rollout step 3 keeps that fallback
     * for exactly this reason.
     */
    window_start: z.string().datetime().optional(),
    /** The sole staleness authority (§2.3). Never triggers a counter reset. */
    window_resets_at: z.string().datetime(),
  })
  .strict();

export type EntitlementProjection = z.infer<typeof entitlementProjectionSchema>;

/**
 * `rekor-entitlement-refresh` queue body (§2.4) — Rekor produces, WayAI consumes.
 *
 * Identity only. The consumer self-loads OrgDO state, lets the lazy free-plan
 * rollover run, rebuilds the projection and pushes it, so it never reads what Rekor
 * observed. `reason` carries the consumer's log line, tells "the window rolled" apart from
 * "the projection pipeline is broken" in an incident, and SELECTS the push trigger — see
 * `TRIGGER_BY_REASON` in the consumer. Adding a value here is therefore a behavioral change
 * on the recovery path, not a new log string.
 *
 * Deliberately NOT `.strict()`: the producer is a separate repo on its own deploy
 * cadence, and an added field must not dead-letter every nudge — the recovery path
 * §2.3 makes the ONLY thing that clears staleness.
 */
export const rekorEntitlementRefreshMessageSchema = z.object({
  org_id: z.string().uuid(),
  reason: z.enum(['window_elapsed', 'projection_missing', 'projection_malformed']),
});

export type RekorEntitlementRefreshMessage = z.infer<typeof rekorEntitlementRefreshMessageSchema>;
